'use client'

import { ColumnDef } from "@tanstack/react-table"
import { ArrowUpDown, Calendar, ExternalLink, FileText, DollarSign, Tag, LinkIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Badge } from '@/components/ui/badge'
// Define necessary types inline to avoid import issues
type TransactionType = 'DEBIT' | 'CREDIT';
type Currency = 'USD' | 'ZWG' | 'ZAR' | string;

interface ChartOfAccount {
  id?: number;
  publicId: string;
  code: string;
  name: string;
  type: string;
}

interface AccountTransaction {
  id?: number;
  publicId: string;
  businessId: number;
  chartOfAccountId: string;
  transactionType: TransactionType;
  amount: number;
  currency: Currency;
  description?: string;
  reference?: string;
  referenceId?: string;
  referencePublicId?: string;
  referenceType?: string;
  transactionDate: string;
  chartOfAccount?: ChartOfAccount;
  userName?: string;
}
import { format, parseISO } from "date-fns"

export const columns: ColumnDef<AccountTransaction>[] = [
  {
    accessorKey: "transactionDate",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Date
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const date = parseISO(cell.getValue() as string);
      return (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 mr-2 text-slate-400" />
          <span className="text-slate-700 dark:text-slate-300">
            {format(date, "MMM d, yyyy")}
          </span>
        </div>
      );
    },
  },
  {
    accessorKey: "chartOfAccount.name",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Account
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ row }) => {
      const accountName = row.original.chartOfAccount?.name || "Unknown Account";
      const accountCode = row.original.chartOfAccount?.code || "";
      const accountPublicId = row.original.chartOfAccount?.publicId;
      
      return (
        <div className="font-medium text-slate-900 dark:text-slate-100">
          {accountPublicId ? (
            <a 
              href={`/chart-of-accounts/${accountPublicId}`} 
              className="hover:text-blue-600 hover:underline"
            >
              {accountName}
            </a>
          ) : (
            <div>{accountName}</div>
          )}
          {accountCode && (
            <div className="text-xs text-slate-500 dark:text-slate-400 font-mono">
              {accountCode}
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "description",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white whitespace-nowrap"
      >
        Description
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => (
      <div className="text-slate-700 dark:text-slate-300 max-w-[200px] truncate">
        {cell.getValue() as string || "-"}
      </div>
    ),
  },
  {
    accessorKey: "referenceType",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Reference
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ row }) => {
      const referenceType = row.original.referenceType || null;
      const referenceId = row.original.referenceId;
      const referencePublicId = row.original.referencePublicId;
      const reference = row.original.reference;
      
      if (!referenceType) return <span className="text-slate-400">-</span>;
      
      const getRefLink = () => {
        if (!referenceId) return "#";
        switch(referenceType) {
          case "invoice": return `/invoice/${referencePublicId}`;
          case "receipt": return `/receipt/${referenceId}`;
          case "bill": return `/bill/${referencePublicId}`;
          case "quotation": return `/quotation/${referencePublicId}`;
          default: return "#";
        }
      };
      
      const badgeConfig: Record<string, { color: string, label: string }> = {
        'invoice': {
          color: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400 border-purple-200 dark:border-purple-800',
          label: 'Invoice'
        },
        'receipt': {
          color: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-400 border-green-200 dark:border-green-800',
          label: 'Receipt'
        }, 
        'bill': {
          color: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800',
          label: 'Bill'
        },
        'quotation': {
          color: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800',
          label: 'Quotation'
        },
        'manual': {
          color: 'bg-slate-50 text-slate-700 dark:bg-slate-900/30 dark:text-slate-400 border-slate-200 dark:border-slate-800',
          label: 'Manual Entry'
        }
      };
      
      const config = badgeConfig[referenceType] || badgeConfig['manual'];
      
      return (
        <div className="space-y-1">
          <Badge variant="outline" className={`font-medium text-xs ${config.color}`}>
            {config.label}
          </Badge>
          {reference && (
            <div className="flex items-center text-sm">
              <LinkIcon className="w-3 h-3 mr-1 text-slate-400" />
              {referenceId ? (
                <a href={getRefLink()} className="text-primary hover:underline">
                  {reference}
                </a>
              ) : (
                <span>{reference}</span>
              )}
            </div>
          )}
        </div>
      );
    },
  },
  {
    accessorKey: "transactionType",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Type
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ cell }) => {
      const transactionType = cell.getValue() as TransactionType;
      
      return (
        <Badge 
          variant="outline" 
          className={transactionType === "DEBIT" 
            ? "bg-amber-50 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200 dark:border-amber-800"
            : "bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800"
          }
        >
          {transactionType}
        </Badge>
      );
    },
  },
  {
    accessorKey: "amount",
    header: ({ column }) => (
      <Button
        variant="ghost"
        onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        className="font-medium text-slate-800 dark:text-slate-200 hover:text-slate-900 dark:hover:text-white"
      >
        Amount
        <ArrowUpDown className="w-4 h-4 ml-2 opacity-70" />
      </Button>
    ),
    cell: ({ row }) => {
      const amount = row.original.amount; // Assuming amount is stored in cents
      const currency = row.original.currency as Currency;
      const transactionType = row.original.transactionType;
      
      return (
        <div className="flex items-center font-medium">
          <DollarSign className="w-4 h-4 mr-1 text-slate-400" />
          <span className={transactionType === "DEBIT" ? "text-amber-600" : "text-blue-600"}>
            {currency} {amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>
      );
    },
  },
  {
    id: "actions",
    header: "Actions",
    cell: ({ row }) => {
      return (
        <div className="flex items-center gap-2">
          <Button 
            onClick={() => window.location.href = `/transactions/${row.original.publicId}`} 
            variant="outline"
            className="h-8 px-3 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700 hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ExternalLink className="w-3.5 h-3.5 mr-2" />
            View
          </Button>
        </div>
      );
    }
  }
] 